"use client";
import { useRouter } from 'next/navigation';
import useCart from '@/hooks/useCart';
import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef, FormEvent, useCallback } from "react";
import {
    ShoppingCart,
    Search,
    User,
    X,
    Tag,
    Sparkles,
} from "lucide-react";
import MegaMenu from "@/components/ui/MegaMenu";
import { cn } from "@/lib/utils";
import CurrencyDropdown from '../ui/CurrencyDropdown';
import { GET_NAV_CATEGORIES_AND_SUBCATEGORIES, SEARCH_BY_CODE, HEADER_SEARCH, HEADER_SEARCH_WORDS } from '@/graphql/queries';
import { useQuery, useApolloClient } from '@apollo/client';
import MobileCategorySlider from './MobileCategorySlider';
import MobileMenu from './MobileSidebar';
import { LanguageSwitcher } from './lang-switcher';
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { clearCart } from "@/redux/cartSlice";
import { AWS_URL } from "@/constants";

// ─── Types ────────────────────────────────────────────────────────────────────
interface SubCategory {
    id: string;
    name: string;
    deleted_at?: string | null;
    fileByImageId?: { bucket: string; visibility: string; cloud: string; path: string } | null;
}

interface Category {
    id: string;
    name: string;
    file?: { bucket: string; visibility: string; cloud: string; path: string } | null;
    sub_categories?: SubCategory[];
}

interface GroupCategory {
    sort_order: number;
    category_id: string;
    category: Category;
}

interface NavigationGroup {
    id: string;
    label: string;
    slug: string;
    sort_order: number;
    navigation_group_categories: GroupCategory[];
}

interface SearchResult {
    type: 'category' | 'subcategory' | 'product' | 'gemstone';
    id: string;
    name: string;
    categoryName?: string;
    href: string;
    imageUrl?: string;
    code?: string;
    matchedCode?: string; // the stone_code or product_code that matched
}

const PRODUCT_CODE_RE = /^[A-Za-z]+[0-9]+(-[0-9]+)+$/;  // RN330-1  (hyphen) → product_code
const ITEM_CODE_RE = /^[A-Za-z]+[0-9]+$/;          // RN3300   → item_code
const DEBOUNCE_MS      = 250;
const MIN_QUERY_LENGTH = 2;   // 1 letter matches almost everything — wait for 2
const MAX_CATEGORIES   = 3;
const MAX_GEMSTONES    = 3;
const MAX_PRODUCTS     = 6;
const PER_BUCKET_LIMIT = 8;

// Escape LIKE wildcards typed by the user (%, _ and \)
const escapeLike = (s: string): string => s.replace(/[\\%_]/g, (m) => `\\${m}`);

// Escape regex metacharacters (for _iregex word matching)
const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// ─── Utilities ────────────────────────────────────────────────────────────────
const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');
};

const getImageUrl = (product: any): string | undefined => {
    const path = product?.product_media?.[0]?.media?.path;
    return path ? `${AWS_URL}${path}` : undefined;
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function Header() {
    const { data } = useQuery(GET_NAV_CATEGORIES_AND_SUBCATEGORIES, { fetchPolicy: "cache-first" });

    const { cartItems } = useCart();
    const router        = useRouter();
    const dispatch      = useDispatch();
    const headerRef     = useRef<HTMLElement>(null);
    const menuTimeoutRef        = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchDebounceRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
    const desktopSearchInputRef = useRef<HTMLInputElement>(null);
    const mobileSearchInputRef  = useRef<HTMLInputElement>(null);

    const [activeMenu, setActiveMenu]             = useState<string | null>(null);
    const [activeGroupId, setActiveGroupId]       = useState<string | null>(null);
    const [searchQuery, setSearchQuery]           = useState("");
    const [debouncedQuery, setDebouncedQuery]     = useState("");
    const [isSearchOpen, setIsSearchOpen]         = useState(false);
    const [searchResults, setSearchResults]       = useState<SearchResult[]>([]);
    const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const token     = Cookies.get("authToken");
    const isLoggedIn = !!token;

    const navigationGroups: NavigationGroup[] = (data?.tenant_navigation_groups || [])
        .slice()
        .sort((a: NavigationGroup, b: NavigationGroup) => a.sort_order - b.sort_order);

    const allCategoriesForSearch = navigationGroups.flatMap(g =>
        g.navigation_group_categories.map(gc => gc.category)
    );

// ─── Search ───────────────────────────────────────────────────────────────
    // Queries go through the Apollo client directly (cache-first), so typing
    // back to an earlier term is instant. requestIdRef drops responses that
    // arrive after the user has already typed something newer.
    const apolloClient = useApolloClient();
    const requestIdRef = useRef(0);
    const [isSearchLoading, setIsSearchLoading] = useState(false);

    const toProductResult = (p: any, code?: string): SearchResult => ({
        type:     "product",
        id:       p.id,
        name:     p.name,
        href:     `/product-detail/${p.id}`,
        imageUrl: getImageUrl(p),
        code:     code ?? p.new_item_code ?? p.item_code ?? p.product_code,
    });

    // RN330-1 → product_code (pro_code / new_pro_code)
    const searchByProductCode = async (q: string): Promise<SearchResult[]> => {
        const upper = q.toUpperCase();
        const like  = `%${escapeLike(upper)}%`;
        const { data } = await apolloClient.query({
            query: SEARCH_BY_CODE,
            variables: {
                where: {
                    product_variant_sizes: {
                        _or: [
                            { pro_code: { _ilike: like } },
                            { new_pro_code: { _ilike: like } },
                        ],
                    },
                },
            },
            fetchPolicy: "cache-first",
        });
        return (data?.product_products ?? []).map((p: any) => {
            const sizes = p.product_variant_sizes ?? [];
            const matched = sizes.find((v: any) =>
                v.pro_code?.toUpperCase().includes(upper) ||
                v.new_pro_code?.toUpperCase().includes(upper)
            );
            const matchedCode =
                (matched?.pro_code?.toUpperCase().includes(upper) ? matched?.pro_code : matched?.new_pro_code) ??
                sizes[0]?.pro_code ??
                sizes[0]?.new_pro_code;
            return toProductResult(p, matchedCode);
        });
    };

    // RN3300 → exact item_code / new_item_code
    const searchByItemCode = async (q: string): Promise<SearchResult[]> => {
        const upper = q.toUpperCase();
        const exact = escapeLike(upper);
        const { data } = await apolloClient.query({
            query: SEARCH_BY_CODE,
            variables: {
                where: {
                    _or: [
                        { item_code: { _ilike: exact } },
                        { new_item_code: { _ilike: exact } },
                    ],
                },
            },
            fetchPolicy: "cache-first",
        });
        return (data?.product_products ?? []).map((p: any) =>
            toProductResult(p, p.item_code?.toUpperCase() === upper ? p.item_code : p.new_item_code ?? p.item_code)
        );
    };

    // Plain text → categories (local) + gemstones + products, ONE request
    const searchByText = async (q: string): Promise<SearchResult[]> => {
        const lower = q.toLowerCase();

        // Categories / sub-categories from the nav data already in memory
        const categoryResults: SearchResult[] = [];
        allCategoriesForSearch.forEach((category) => {
            if (category.name.toLowerCase().includes(lower)) {
                categoryResults.push({
                    type: "category",
                    id:   category.id,
                    name: category.name,
                    href: `/shop-category/${category.id}`,
                });
            }
            category.sub_categories?.forEach((sub) => {
                if (sub.name.toLowerCase().includes(lower)) {
                    categoryResults.push({
                        type:         "subcategory",
                        id:           sub.id,
                        name:         sub.name,
                        categoryName: category.name,
                        href:         `/shop-category/${category.id}`,
                    });
                }
            });
        });

        const escaped = escapeLike(q);
        const words   = q.split(/\s+/).filter(Boolean);

        // Whole phrase (fast, ~250ms) — finds "blue topaz", codes, exact names
        const phraseSearch = apolloClient.query({
            query: HEADER_SEARCH,
            variables: {
                name:     `%${escaped}%`,
                code:     `%${escaped.toUpperCase()}%`,
                limit:    PER_BUCKET_LIMIT,
                gemLimit: MAX_GEMSTONES,
            },
            fetchPolicy: "cache-first",
        });

        // Multi-word ("peridot bracelets"): every word must match the name,
        // category, sub-category or a gem — same rule as /shop on Enter, so the
        // suggestions show the same products the results page will.
        const wordsSearch = words.length > 1
            ? apolloClient.query({
                query: HEADER_SEARCH_WORDS,
                variables: {
                    where: {
                        _and: words.map((w) => {
                            const pattern = `\\y${escapeRegex(w)}\\y`; // Postgres word boundary
                            return {
                                _or: [
                                    { name: { _iregex: pattern } },
                                    { category: { name: { _iregex: pattern } } },
                                    { sub_category: { name: { _iregex: pattern } } },
                                    { product_variations: { product_variation_gems: { gem: { name: { _iregex: pattern } } } } },
                                ],
                            };
                        }),
                    },
                    // gem named exactly like a typed word ("peridot" → Peridot, case-insensitive)
                    // plus the exact phrase ("blue topaz" → Blue Topaz)
                    gemWhere: { _or: [q, ...words].map((w) => ({ name: { _ilike: escapeLike(w) } })) },
                    limit:    MAX_PRODUCTS,
                    gemLimit: MAX_GEMSTONES,
                },
                fetchPolicy: "cache-first",
            })
            : Promise.resolve(null);

        const [{ data }, wordsResult] = await Promise.all([phraseSearch, wordsSearch]);
        const wordsData = wordsResult?.data;

        // Merge in priority order (phrase buckets, then word matches), de-duplicated by id
        const seen = new Set<string>();
        const products: any[] = [];
        const addProducts = (list: any[] | undefined) => {
            for (const p of list ?? []) {
                if (!p?.id || seen.has(p.id)) continue;
                seen.add(p.id);
                products.push(p);
            }
        };
        for (const bucket of ["by_name", "by_item_code", "by_pro_code"]) addProducts(data?.[bucket]);
        addProducts(wordsData?.product_products);
        for (const bucket of ["by_variation", "by_gem"]) addProducts(data?.[bucket]);

        // Category / sub-category suggestions derived from matched products
        const seenCatIds  = new Set(categoryResults.map((c) => c.id));
        const displayTerm = q.charAt(0).toUpperCase() + q.slice(1); // "amethyst" → "Amethyst"
        products.forEach((p: any) => {
            if (p.category?.id && !seenCatIds.has(p.category.id)) {
                seenCatIds.add(p.category.id);
                categoryResults.push({
                    type: "category",
                    id:   p.category.id,
                    name: `${displayTerm} in ${p.category.name}`,
                    href: `/shop?search=${encodeURIComponent(q)}&category=${p.category.id}`,
                });
            }
            if (p.sub_category?.id && !seenCatIds.has(p.sub_category.id)) {
                seenCatIds.add(p.sub_category.id);
                categoryResults.push({
                    type:         "subcategory",
                    id:           p.sub_category.id,
                    name:         `${displayTerm} in ${p.sub_category.name}`,
                    categoryName: p.category?.name,
                    href:         `/shop?search=${encodeURIComponent(q)}&category=${p.category?.id}&subcategory=${p.sub_category.id}`,
                });
            }
        });

        // Gems: phrase matches first, then word matches — a gem named exactly
        // like a typed word ("Peridot") ahead of combos ("Agate,Garnet,Peridot")
        const lowerWords = words.map((w) => w.toLowerCase());
        const gemRank = (name: string) => {
            const n = (name || "").toLowerCase().trim();
            if (n === lower) return 0;
            if (lowerWords.includes(n)) return 1;
            return n.includes(",") ? 3 : 2;
        };
        // de-duplicate by name too — master_gems has repeated names with different ids
        const seenGemNames = new Set<string>();
        const gems = [...(data?.master_gems ?? []), ...(wordsData?.master_gems ?? [])]
            .filter((g: any) => {
                const key = (g?.name || "").toLowerCase().replace(/\s+/g, "");
                if (!g?.id || !key || seenGemNames.has(key)) return false;
                seenGemNames.add(key);
                return true;
            })
            .sort((a: any, b: any) => gemRank(a.name) - gemRank(b.name));

        const gemstoneResults: SearchResult[] = gems.map((gem: any) => ({
            type: "gemstone" as const,
            id:   gem.id,
            name: gem.name,
            href: `/shop?gemstone=${slugify(gem.name)}`,
        }));

        return [
            ...categoryResults.slice(0, MAX_CATEGORIES),
            ...gemstoneResults.slice(0, MAX_GEMSTONES),
            ...products.slice(0, MAX_PRODUCTS).map((p) => toProductResult(p)),
        ];
    };

    const runSearch = async (q: string): Promise<SearchResult[]> => {
        if (PRODUCT_CODE_RE.test(q)) return searchByProductCode(q);
        if (ITEM_CODE_RE.test(q)) {
            const exact = await searchByItemCode(q);
            if (exact.length) return exact;
            // e.g. "RN33" (partial code) or "ring925" — fall back to the broad search
        }
        return searchByText(q);
    };

    // ─── Debounce search query ────────────────────────────────────────────────
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, DEBOUNCE_MS);
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, [searchQuery]);

    // ─── Run search when debounced query changes ──────────────────────────────
    useEffect(() => {
        const q = debouncedQuery.trim();
        const requestId = ++requestIdRef.current;

        if (q.length < MIN_QUERY_LENGTH) {
            setSearchResults([]);
            setIsSearchLoading(false);
            if (!q) setIsSearchDrawerOpen(false);
            return;
        }

        setIsSearchLoading(true);
        runSearch(q)
            .then((results) => {
                if (requestId !== requestIdRef.current) return; // stale
                setSearchResults(results);
                setIsSearchDrawerOpen(true);
            })
            .catch((err) => {
                if (requestId !== requestIdRef.current) return;
                console.error("Header search failed:", err?.message ?? err);
                setSearchResults([]);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setIsSearchLoading(false);
            });
    }, [debouncedQuery]); // eslint-disable-line react-hooks/exhaustive-deps


    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleMenuEnter = (label: string, groupId: string) => {
        if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
        setActiveMenu(label);
        setActiveGroupId(groupId);
         setIsSearchDrawerOpen(false);
    };

    const handleMenuLeave = () => {
        menuTimeoutRef.current = setTimeout(() => {
            setActiveMenu(null);
            setActiveGroupId(null);
        }, 100);
    };

    const handleMenuNavigate = () => {
        setActiveMenu(null);
        setActiveGroupId(null);
    };

    const handleSearch = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        submitSearch(searchQuery);
    };

    const handleSearchIconClick = () => submitSearch(searchQuery);

const submitSearch = (query: string) => {
    const q = query.trim();
    if (!q) return;

router.push(`/shop?search=${encodeURIComponent(q)}`);
    setSearchQuery("");
    setIsSearchDrawerOpen(false);
    setSearchResults([]);
};

    const handleSearchResultClick = (href: string) => {
        router.push(href);
        setSearchQuery("");
        setIsSearchOpen(false);
        setIsSearchDrawerOpen(false);
        setSearchResults([]);
    };

    const closeSearch = () => {
        setIsSearchOpen(false);
        setSearchQuery("");
        setIsSearchDrawerOpen(false);
        setSearchResults([]);
    };

    const handleCartClick = () => {
        if (!isLoggedIn) dispatch(clearCart());
        router.push("/cart");
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
                setActiveMenu(null);
                setActiveGroupId(null);
                setIsSearchDrawerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isSearchOpen) {
            const t = setTimeout(() => mobileSearchInputRef.current?.focus(), 150);
            return () => clearTimeout(t);
        }
    }, [isSearchOpen]);

    const activeGroup           = navigationGroups.find(g => g.id === activeGroupId);
    const activeGroupCategories = activeGroup
        ? activeGroup.navigation_group_categories
            .slice()
            .sort((a, b) => a.sort_order - b.sort_order)
            .map(gc => gc.category)
        : [];

    const cartCount = isLoggedIn
        ? (cartItems?.reduce((total, item) => {
            if (item.wholesaleSelections?.length > 0) {
                return total + item.wholesaleSelections.reduce((s: number, sel: any) => s + (sel.qty || 0), 0);
            }
            return total + (item.quantity || 0);
        }, 0) ?? 0)
        : 0;

    // ─── Grouped search results by type ────────────────────────────────────────
    const groupedResults = {
        products: searchResults.filter(r => r.type === 'product'),
        gemstones: searchResults.filter(r => r.type === 'gemstone'),
        categories: searchResults.filter(r => r.type === 'category' || r.type === 'subcategory'),
    };

    // ─── Search suggestions dropdown (shared between desktop + mobile) ────────
    // A render function, not a nested component: a component defined inside
    // Header is a new type on every render, so React remounted the whole
    // dropdown (and reloaded its images) on each keystroke.
    const renderSearchSuggestions = (results: SearchResult[]) => {
        const hasResults = results.length > 0;
        const typed      = searchQuery.trim();
        const tooShort   = typed.length > 0 && typed.length < MIN_QUERY_LENGTH;
        const pending    = !tooShort && typed !== debouncedQuery.trim(); // debounce not fired yet

        return (
            <div className="py-1.5 z-[9999]">
                {tooShort && (
                    <p className="text-[11px] text-stone-400 px-4 py-2">Keep typing…</p>
                )}
                {!tooShort && (isSearchLoading || (pending && !hasResults)) && (
                    <p className="text-[11px] text-stone-400 px-4 py-2">Searching…</p>
                )}
                {!tooShort && !isSearchLoading && !pending && !hasResults && debouncedQuery.trim() && (
                    <p className="text-[11px] text-stone-400 px-4 py-2">No results found</p>
                )}
                
                {/* ── GEMSTONES SECTION ── */}
                {groupedResults.gemstones.length > 0 && (
                    <>
                        <div className="px-4 pt-3 pb-2">
                            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest flex items-center gap-2">
                                <Sparkles className="w-3 h-3 text-[#a8704e]" />
                                Gemstones
                            </p>
                        </div>
                        {groupedResults.gemstones.map((result) => (
                            <button
                                key={`${result.type}-${result.id}`}
                                onClick={() => handleSearchResultClick(result.href)}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#faf5f0] transition-colors flex items-center gap-3 group/item"
                            >
                                <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#c9a96e]/20 to-[#a8704e]/20 flex items-center justify-center flex-shrink-0 group-hover/item:from-[#c9a96e]/30 group-hover/item:to-[#a8704e]/30 transition-colors">
                                    <Sparkles className="h-3.5 w-3.5 text-[#a8704e]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] font-medium text-stone-700">{result.name}</p>
                                    <p className="text-[10px] text-stone-400">Gemstone</p>
                                </div>
                            </button>
                        ))}
                    </>
                )}

                {/* ── PRODUCTS SECTION ── */}
                {groupedResults.products.length > 0 && (
                    <>
                        <div className="px-4 pt-3 pb-2">
                            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Products</p>
                        </div>
                        {groupedResults.products.map((result) => (
                            <button
                                key={`${result.type}-${result.id}`}
                                onClick={() => handleSearchResultClick(result.href)}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#faf5f0] transition-colors flex items-center gap-3 group/item"
                            >
                                <div className="w-8 h-8 rounded-md bg-[#f5ede4] flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#e8d5c0] transition-colors overflow-hidden">
                                    {result.imageUrl ? (
                                        <img
                                            src={result.imageUrl}
                                            alt={result.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Search className="h-3 w-3 text-[#a8704e]" />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] font-medium text-stone-700 truncate">{result.name}</p>
                                    {result.code && (
                                        <span className="text-[11px] text-stone-400">{result.code}</span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </>
                )}

                {/* ── CATEGORIES SECTION ── */}
                {groupedResults.categories.length > 0 && (
                    <>
                        <div className="px-4 pt-3 pb-2">
                            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Categories</p>
                        </div>
                        {groupedResults.categories.map((result) => (
                            <button
                                key={`${result.type}-${result.id}`}
                                onClick={() => handleSearchResultClick(result.href)}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#faf5f0] transition-colors flex items-center gap-3 group/item"
                            >
                                <div className="w-8 h-8 rounded-md bg-[#f5ede4] flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#e8d5c0] transition-colors">
                                    <Search className="h-3 w-3 text-[#a8704e]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] font-medium text-stone-700 truncate">{result.name}</p>
                                    {result.type === "subcategory" && (
                                        <p className="text-[11px] text-stone-400">in {result.categoryName}</p>
                                    )}
                                </div>
                            </button>
                        ))}
                    </>
                )}
            </div>
        );
    };

    // ─── JSX ──────────────────────────────────────────────────────────────────
    return (
        <>
            <header
                ref={headerRef}
                className="w-full z-50 sticky lg:top-8 top-[28px] left-0 right-0 bg-white shadow-[0_1px_0_0_#e7e0d8,0_2px_8px_0_rgba(0,0,0,0.06)] transition-all duration-300"
            >
                {/* ── Main bar ── */}
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
                    <div className="flex items-center h-[58px] lg:h-[66px] gap-3 lg:gap-0">

                        {/* LEFT */}
                        <div className="flex items-center gap-2.5 flex-shrink-0 lg:w-[220px]">
                            <div className="lg:hidden">
                                <MobileMenu
                                    navigationGroups={navigationGroups}
                                    isOpen={isMobileMenuOpen}
                                    onOpenChange={setIsMobileMenuOpen}
                                />
                            </div>
                            <Link href="/" className="flex items-center hover:opacity-75 transition-opacity duration-200">
                                <Image
                                    src="/logo-png.png"
                                    alt="Silver & Gems"
                                    width={32}
                                    height={32}
                                    className="h-8 w-auto lg:hidden"
                                    priority
                                />
                                <Image
                                    src="https://jewel-ecom.s3.ap-south-1.amazonaws.com/pub/development/icons/2026-03-02/06e7915b-6e9f-4fa9-86d1-e31b9bfa9262"
                                    alt="Silver & Gems"
                                    width={140}
                                    height={42}
                                    className="h-[38px] w-auto hidden lg:block"
                                    priority
                                />
                            </Link>
                        </div>

                        <div className="hidden lg:block">
                            <LanguageSwitcher />
                        </div>

                        {/* CENTER — desktop search */}
                        <div className="hidden lg:flex flex-1 justify-center px-8">
                            <div className="relative w-full max-w-[500px]">
                                <form onSubmit={handleSearch}>
                                    <div className="relative group">
                                        <input
                                            ref={desktopSearchInputRef}
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                if (e.target.value.trim()) setIsSearchDrawerOpen(true);
                                                else setIsSearchDrawerOpen(false);
                                            }}
                                            onFocus={(e) => {
                                                if (e.target.value.trim()) setIsSearchDrawerOpen(true);
                                            }}
                                            placeholder="Search products, gemstones, categories..."
                                            className="w-full pl-4 pr-20 py-2 text-[13px] rounded-full bg-[#f5f0eb] text-stone-700 placeholder-stone-400 border border-[#e8ddd4] hover:border-[#d4c4b0] focus:border-[#c5a08a] focus:bg-white focus:ring-[3px] focus:ring-[#a8704e]/10 focus:outline-none transition-all duration-200"
                                        />
                                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                            {searchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={() => { setSearchQuery(""); setIsSearchDrawerOpen(false); setSearchResults([]); }}
                                                    className="p-1.5 text-stone-400 hover:text-stone-600 transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={handleSearchIconClick}
                                                className="p-1.5 rounded-full bg-[#a8704e] hover:bg-[#8f5c3e] text-white transition-colors"
                                            >
                                                <Search className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </form>

                                {/* Desktop suggestions dropdown */}
                                {(isSearchDrawerOpen || isSearchLoading) && searchQuery.trim() && (
                                    <div className="absolute top-[calc(100%+6px)] left-0 right-0 bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-stone-100 max-h-[360px] overflow-y-auto z-50">
                                        {renderSearchSuggestions(searchResults)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* RIGHT — icons */}
                        <div className="flex items-center gap-0.5 ml-auto lg:ml-0 lg:w-[220px] lg:justify-end flex-shrink-0">
                            <CurrencyDropdown shouldBeWhite={true} />

                            <button
                                onClick={() => setIsSearchOpen(!isSearchOpen)}
                                className="lg:hidden p-2 rounded-lg hover:bg-stone-100 transition-colors text-stone-600"
                                aria-label="Search"
                            >
                                <Search className="h-[19px] w-[19px]" />
                            </button>

                            <button
                                onClick={() => router.push("/user")}
                                className="p-2 rounded-lg hover:bg-stone-100 transition-colors text-stone-600"
                                aria-label="Account"
                            >
                                <User className="h-[19px] w-[19px]" />
                            </button>

                            <button
                                onClick={handleCartClick}
                                className="relative p-2 rounded-lg hover:bg-stone-100 transition-colors text-stone-600"
                                aria-label="Cart"
                            >
                                <ShoppingCart className="h-[19px] w-[19px]" />
                                {isLoggedIn && cartCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 bg-[#7c3b1e] text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-[3px] flex items-center justify-center leading-none border-[1.5px] border-white">
                                        {cartCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Mobile search drawer ── */}
                {isSearchOpen && (
                    <div className="lg:hidden border-t border-stone-100 bg-white px-4 py-3">
                        <form onSubmit={handleSearch}>
                            <div className="relative">
                                <input
                                    ref={mobileSearchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        if (e.target.value.trim()) setIsSearchDrawerOpen(true);
                                        else setIsSearchDrawerOpen(false);
                                    }}
                                    onFocus={(e) => {
                                        if (e.target.value.trim()) setIsSearchDrawerOpen(true);
                                    }}
                                    placeholder="Search products, gemstones..."
                                    className="w-full pl-4 pr-20 py-2.5 rounded-full text-sm bg-[#f5f0eb] text-stone-700 placeholder-stone-400 border border-[#e8ddd4] focus:border-[#c5a08a] focus:bg-white focus:ring-2 focus:ring-[#a8704e]/10 focus:outline-none transition-all"
                                />
                                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                    {searchQuery && (
                                        <button type="button" onClick={closeSearch} className="p-1.5 text-stone-400 hover:text-stone-600">
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleSearchIconClick}
                                        className="p-1.5 rounded-full bg-[#a8704e] hover:bg-[#8f5c3e] text-white transition-colors"
                                    >
                                        <Search className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </form>

                        {/* Mobile suggestions */}
                        {(isSearchDrawerOpen || isSearchLoading) && searchQuery.trim() && (
                            <div className="mt-2 bg-white rounded-xl shadow-lg border border-stone-100 max-h-64 overflow-y-auto">
                                {renderSearchSuggestions(searchResults)}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Desktop nav bar ── */}
                <div className="relative">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
                        <nav className="hidden lg:flex items-center justify-center">
                            {navigationGroups.map((group) => (
                                <div
                                    key={group.id}
                                    className="relative"
                                    onMouseEnter={() => handleMenuEnter(group.label, group.id)}
                                    onMouseLeave={handleMenuLeave}
                                >
                                    <button
                                        className={cn(
                                            "relative px-5 py-3 text-[11px] font-bold tracking-[0.15em] uppercase transition-colors duration-200",
                                            activeMenu === group.label
                                                ? "text-[#64483c]"
                                                : "text-stone-500 hover:text-stone-700"
                                        )}
                                    >
                                        {group.label}
                                        <span
                                            className={cn(
                                                "absolute bottom-0 left-5 right-5 h-[2px] bg-[#a8704e] rounded-full transition-all duration-300 origin-center",
                                                activeMenu === group.label ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"
                                            )}
                                        />
                                    </button>
                                </div>
                            ))}
                        </nav>
                    </div>

                    {activeMenu && activeGroupId && activeGroupCategories.length > 0 && (
                        <div
                            className="absolute left-0 right-0 w-full bg-white shadow-2xl border-t border-[#ede8e2] z-50"
                            onMouseEnter={() => handleMenuEnter(activeMenu, activeGroupId)}
                            onMouseLeave={handleMenuLeave}
                        >
                            <MegaMenu
                                categories={activeGroupCategories}
                                onNavigate={handleMenuNavigate}
                            />
                        </div>
                    )}
                </div>
            </header>

            <MobileCategorySlider />
        </>
    );
}